import * as React from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';
import { injectIntl } from 'react-intl';
import type { WrappedComponentProps } from 'react-intl';
import { v4 } from 'uuid';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { fetchCmdbObjectsWithSignal } from '../../rest/cmdb-object-picker';
import type {
	CmdbObject,
	CmdbObjectAttribute,
	CmdbObjectResponse,
	ObjectAttributeValue,
} from '../../rest/cmdb-object-picker';
import { PaginatedObjectsView } from '../cmdb-object-picker-new';
import type { CmdbObjectPickerDumbProps, CmdbObjectPickerOption } from './cmdb-object-picker-dumb';
import { CmdbObjectPickerDumb } from './cmdb-object-picker-dumb';
import type { InputActionMeta } from './cmdb-object-picker-dumb/cmdb-object-picker-dumb';
import messages from './messages';

const NEW_EXPERIENCE_GATE = 'assets_custom_fields_limit_increase_ga';

const isNewExperienceEnabled = (): boolean => FeatureGates.checkGate(NEW_EXPERIENCE_GATE);

interface Props extends CmdbObjectPickerDumbProps {
	isMulti: boolean;
	filterAttributes: string[];
	portalId: number;
	requestTypeId: number;
	fieldValueMap: string[][];
	placeholderValues: { [key: string]: string };
	initialValues?: CmdbObjectPickerOption[];
}

interface CmdbObjectPickerState {
	options: CmdbObjectPickerOption[];
	values: CmdbObjectPickerOption[];
	loading: boolean;
	responseTotal: number;
	selectedOptions: string[];
	flags: string[];
	hasNextPage: boolean;
	lastSearchedInputValue: string;
}

const MAX_OBJECT_COUNT_CUTOFF = 1_000;

type CmdbObjectPickerProps = Omit<
	Props,
	| 'loadOptions'
	| 'doInitialSearch'
	| 'options'
	| 'onInputChange'
	| 'key'
	| 'onFocus'
	| 'defaultValue'
	| 'onMenuScrollToBottom'
>;

export class CmdbObjectPicker extends React.Component<
	CmdbObjectPickerProps & WrappedComponentProps,
	CmdbObjectPickerState
> {
	state: CmdbObjectPickerState = {
		options: [],
		values: [],
		loading: false,
		responseTotal: 0,
		selectedOptions: [],
		flags: [],
		hasNextPage: false,
		lastSearchedInputValue: '',
	};
	private abortController: AbortController | null = null;

	componentDidMount() {
		if (this.props.populateDefaultValue) {
			// Disabling existing violations, should be fixed when revisited.
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.getObjectOptions(() => this.fetchFunction(''), true);
		}

		if (isJsmPortalRovoIntegrationEnabled()) {
			if (this.props.initialValues) {
				this.setState({
					values: this.props.initialValues,
				});
			}
		}
	}

	componentDidUpdate(prevProps: CmdbObjectPickerProps) {
		if (
			this.props.populateDefaultValue &&
			!isEqual(prevProps.placeholderValues, this.props.placeholderValues)
		) {
			// Disabling existing violations, should be fixed when revisited.
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.getObjectOptions(() => this.fetchFunction(''), true);
		}
	}

	getLabel = (total: number): string => {
		const { intl } = this.props;
		return (total ?? 0) >= MAX_OBJECT_COUNT_CUTOFF
			? intl.formatMessage(messages.dropdownMaxNumberOfSearchResultsLabel, {
					maxObjectCountCutoff: MAX_OBJECT_COUNT_CUTOFF,
				})
			: intl.formatMessage(messages.dropdownNumberOfSearchResultsLabel, {
					numberOfSearchResults: total,
				});
	};

	getOptionsWithLabel = (
		options: CmdbObjectPickerOption[],
		numberOfResults: number,
		numberOfAlreadySelectedResults: number,
	) => {
		if (FeatureGates.checkGate('jsm_assets_field_only_display_label_in_portal')) {
			// When isMulti is true, selected values are stripped out from the results dropdown.
			if (this.props.isMulti) {
				return [
					{
						options,
						label: this.getLabel(numberOfResults - numberOfAlreadySelectedResults),
					},
				];
			}
			return [
				{
					options,
					label: this.getLabel(numberOfResults),
				},
			];
		}
		return [
			{
				options,
				label: this.getLabel(numberOfResults - numberOfAlreadySelectedResults),
			},
		];
	};

	// getObjectOptions, fetchFunction retrieveObjects used by FIS FF
	getSearchableObject = (
		cmdbObject: CmdbObject,
		attributesToDisplayInSearch: string[],
	): CmdbObjectPickerOption => {
		const getDisplayValuesFromAttribute = (attribute: CmdbObjectAttribute): string =>
			attribute.objectAttributeValues
				.map((val: ObjectAttributeValue) => val.displayValue)
				.join(' ');

		// This label is the string value that @atlaskit/select uses under the hood
		// to determine a 'match' when typing in the select field. Therefore, we need to
		// build a string that includes values for all the attributes marked as
		// 'searchable' within the Insight field configuration.
		const searchableLabel = cmdbObject.attributes
			.map((attribute) => {
				if (
					attributesToDisplayInSearch.includes(attribute.objectTypeAttribute.name) ||
					(attributesToDisplayInSearch.includes('Label') && attribute.objectTypeAttribute.label)
				) {
					return getDisplayValuesFromAttribute(attribute);
				}
				return undefined;
			})
			.filter(Boolean)
			.join(' ');

		// Label should fall back to the label attribute of the object if it doesn't contain any of the search attributes
		return {
			cmdbObject,
			value: cmdbObject.id,
			label: searchableLabel || cmdbObject.label,
		};
	};

	getObjectOptions = async (
		fetchFunction: () => Promise<CmdbObjectResponse>,
		setValues = false,
	) => {
		const { populateDefaultValue, isMulti, limit, id } = this.props;
		try {
			const response = await fetchFunction();

			const options: CmdbObjectPickerOption[] = response.cmdbObjects.map((object) =>
				this.getSearchableObject(object, this.props.filterAttributes),
			);

			this.setState({ responseTotal: response.total });

			if (populateDefaultValue && setValues) {
				const newValues = this.getDefaultInitialValues(options, isMulti, limit);
				this.setState({
					values: newValues,
				});

				this.props.onChange?.(
					id,
					newValues.map((val) => (val ? val.value : '')),
				);
			}

			this.setState((prevState) => ({
				options: [...prevState.options, ...options],
			}));
		} catch (error) {
			// Ensure the dropdown never gets stuck on the loading spinner if the fetch (or
			// subsequent processing) throws. fetchFunction already handles its own errors, but
			// this guards against any other failure in this method.
			this.setState({ loading: false });
			trackError('cmdb-fetch-error', {}, error as FetchError);
		}
	};

	addErrorFlag = () => {
		const { flags } = this.state;
		const newFlags = [...flags];
		newFlags.splice(0, 0, v4());

		this.setState({
			flags: newFlags,
		});
	};

	dismissErrorFlag = () => {
		const flags = this.state.flags;
		this.setState({ flags: flags.splice(1) });
	};

	handleError = (error: FetchError) => {
		if (error.status === 400) {
			this.addErrorFlag();
		}
	};

	fetchFunction = (
		inputValue: string,
		isNextPageQuery: boolean = false,
	): Promise<CmdbObjectResponse> => {
		// Abort any in-flight requests, and create a new AbortController for the new request.
		if (this.abortController) {
			this.abortController.abort('Cancelling in-flight request');
		}
		const abortController = new AbortController();
		this.abortController = abortController;
		const { signal } = abortController;

		// If fetching a fresh set of values rather than a subsequent page, clear out the options shown in the dropdown.
		if (!isNextPageQuery) {
			this.setState({
				options: [],
			});
		}
		this.setState({
			loading: true,
		});

		return fetchCmdbObjectsWithSignal(
			this.props.portalId,
			this.props.requestTypeId,
			this.props.id,
			inputValue,
			this.props.fieldValueMap,
			signal,
			isNextPageQuery ? this.state.options.length : 0,
		)
			.then((response) => {
				this.setState({
					hasNextPage: response.hasNextPage,
					lastSearchedInputValue: inputValue,
					loading: false,
				});
				return response;
			})
			.catch((err: FetchError) => {
				if (!abortController.signal.aborted) {
					trackError('cmdb-fetch-error', {}, err);
					this.handleError(err);
					this.setState({ loading: false });
				}
				return { cmdbObjects: [], total: 0, hasNextPage: false };
			});
	};

	retrieveObjects = (inputValue: string, type: InputActionMeta) => {
		if (type.action === 'input-change') {
			// Disabling existing violations, should be fixed when revisited.
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.getObjectOptions(() => this.fetchFunction(inputValue));
		}
	};

	onInputChangeDebounced = debounce(this.retrieveObjects, 400);

	onMenuScrollToBottom = async () => {
		if (this.state.hasNextPage) {
			await this.getObjectOptions(() =>
				this.fetchFunction(this.state.lastSearchedInputValue, true),
			);
		}
	};

	onFocus = () => {
		// Disabling existing violations, should be fixed when revisited.
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.getObjectOptions(() => this.fetchFunction(''));
	};

	onChange = (name: string, value: string[]) => {
		this.setState({ selectedOptions: value });
		// The existing code breaks if a search is currently in progress because prevState.options is empty.
		if (FeatureGates.checkGate('jsm_assets_field_only_display_label_in_portal')) {
			this.setState((prevState) => {
				// prevState.values will have existing values, and `prevState.options` will have potential new values.
				// We cannot only rely on prevState.options because they will be empty if search is occurring right now.
				// Get all existing values from prevValues
				const valuesFromExisting = prevState.values.filter((val) => value?.includes(val?.value));
				// Get any new values from options
				const valuesFromSearch = prevState.options.filter((opt) => value?.includes(opt?.value));
				// Combine and dedupe
				const newValues = [...valuesFromExisting, ...valuesFromSearch].filter(
					(obj1, i, arr) => arr.findIndex((obj2) => obj2.value === obj1.value) === i,
				);

				return {
					values: newValues,
				};
			});
		} else {
			this.setState((prevState) => ({
				values: prevState.options.filter((opt) => value?.includes(opt?.value)),
			}));
		}
		this.props.onChange?.(name, value);
	};

	getDefaultInitialValues = (
		options: CmdbObjectPickerOption[],
		isMulti: boolean,
		limit: number,
	): CmdbObjectPickerOption[] => {
		if (
			options.length === 0 &&
			FeatureGates.checkGate('jsm_assets_field_only_display_label_in_portal')
		) {
			return options;
		}
		if (isMulti) {
			return options.length > limit ? options.slice(0, limit - 1) : options;
		}
		return [options[0]];
	};

	// --- New (create-issue style) experience handlers, gated behind NEW_EXPERIENCE_GATE ---

	handleNewSearch = (query: string) => {
		this.setState({ lastSearchedInputValue: query });
		// Disabling existing violations, should be fixed when revisited.
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.getObjectOptions(() => this.fetchFunction(query));
	};

	handleNewLoadMore = () => {
		// Disabling existing violations, should be fixed when revisited.
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.onMenuScrollToBottom();
	};

	handleSelectionCommit = (objects: CmdbObject[]) => {
		const newValues: CmdbObjectPickerOption[] = objects.map((object) =>
			this.getSearchableObject(object, this.props.filterAttributes),
		);
		this.setState({ values: newValues });
		this.props.onChange?.(
			this.props.id,
			newValues.map((val) => val.value),
		);
	};

	renderNewExperience() {
		const { options, loading, values, hasNextPage } = this.state;
		const { isMulti, limit, label, required, errorMessage, filterAttributes } = this.props;
		const showSearchAttributes = FeatureGates.checkGate(
			'assets_custom_fields_limit_increase_ga_fixes',
		);

		const selectedObjects = values
			.map((v) => v.cmdbObject)
			.filter((o): o is CmdbObject => Boolean(o));
		const searchResults = options
			.map((o) => o.cmdbObject)
			.filter((o): o is CmdbObject => Boolean(o));

		return (
			<ErrorBoundary id={'cmdb-object-picker'} packageName={'portal_fields'}>
				<PaginatedObjectsView
					selectedObjects={selectedObjects}
					searchResults={searchResults}
					isMulti={isMulti}
					limit={limit}
					attributesToDisplayInSearch={showSearchAttributes ? filterAttributes : undefined}
					fieldLabel={label}
					searchQuery={this.state.lastSearchedInputValue}
					onSearchChange={this.handleNewSearch}
					isLoading={loading}
					hasMore={hasNextPage}
					onLoadMore={this.handleNewLoadMore}
					onSelectionCommit={this.handleSelectionCommit}
					required={required}
					errorMessage={errorMessage}
				/>
			</ErrorBoundary>
		);
	}

	render() {
		// The new create-issue-style experience only applies to multi-select fields.
		// Single-select CMDB fields always use the original picker.
		if (this.props.isMulti && isNewExperienceEnabled()) {
			return this.renderNewExperience();
		}

		const { options, loading, responseTotal, selectedOptions, values, flags } = this.state;
		const { onChange, populateDefaultValue, intl, ...otherProps } = this.props;

		// This feature gate turns the component into a completely controlled component.
		// Previously it was uncontrolled except for when `populateDefaultValue` was true, making the logic
		// difficult to follow.
		const controlledComponentState = FeatureGates.checkGate(
			'jsm_assets_field_only_display_label_in_portal',
		);

		let numberOfAlreadySelectedSearchResults;
		if (controlledComponentState) {
			// If always a controlled component, then we can just rely on `values` and don't need `selectedOptions`.
			// Please clean up `selectedOptions` when removing jsm_assets_field_only_display_label_in_portal feature gate.
			numberOfAlreadySelectedSearchResults = options.filter((object) =>
				values.some((value) => value.value === object.value),
			).length;
		} else {
			numberOfAlreadySelectedSearchResults = options.filter((object) =>
				selectedOptions.some((option) => option === object.value),
			).length;
		}

		return (
			<ErrorBoundary id={'cmdb-object-picker'} packageName={'portal_fields'}>
				<CmdbObjectPickerDumb
					{...otherProps}
					doInitialSearch
					onInputChange={this.onInputChangeDebounced}
					options={this.getOptionsWithLabel(
						options,
						responseTotal,
						numberOfAlreadySelectedSearchResults,
					)}
					onFocus={this.onFocus}
					isLoading={loading}
					defaultValue={values}
					onChange={populateDefaultValue || controlledComponentState ? this.onChange : onChange}
					populateDefaultValue={populateDefaultValue}
					classNamePrefix="cmdbObjectPickerField"
					onMenuScrollToBottom={this.onMenuScrollToBottom}
				/>
				<FlagGroup onDismissed={this.dismissErrorFlag}>
					{flags.map((flagId) => {
						return (
							<AutoDismissFlag
								id={flagId}
								icon={
									<ErrorIcon
										color={token('color.icon.danger', '#BF2600')}
										label="Error"
										spacing="spacious"
									/>
								}
								key={flagId}
								title={intl.formatMessage(messages.errorFlagTitle)}
								description={intl.formatMessage(messages.errorFlagDescription)}
							/>
						);
					})}
				</FlagGroup>
			</ErrorBoundary>
		);
	}
}

export default injectIntl(CmdbObjectPicker);
